<template>
  <div id="trading-rules" class="fill-height align-start">
    <i-user-action-dialog
      v-if="actionDialog"
      :dialog="actionDialog"
      :title="actionTitle"
      :text="actionText"
      :action="actionType"
      @cancel="cancelAction"
      @confirm="confirmAction"
    />

    <v-dialog
      v-if="showPreviousTradingRules"
      v-model="showPreviousTradingRules"
      max-width="750px"
      persistent
      @click:outside="showPreviousTradingRules = false"
      @keydown.esc="showPreviousTradingRules = false"
    >
      <i-previous-trading-rules
        :rules="previousTradingRules"
        @close="showPreviousTradingRules = false"
      />
    </v-dialog>

    <v-dialog
      v-model="confirmDeletionDialog"
      v-if="confirmDeletionDialog"
      content-class="i-dialog"
      width="400"
      @click:outside="confirmDeletionDialog = false"
      @keydown.esc="confirmDeletionDialog = false"
    >
      <v-card>
        <template>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Confirm Deletion</span>
          </v-card-title>

          <v-card-text class="py-4 text-center">
            Please confirm deletion of upcoming changes, current rules will stay
            in effect.
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="justify-center">
            <v-btn
              rounded
              text
              class="i-btn-text-black"
              @click="confirmDeletionDialog = false"
            >
              <strong>Cancel</strong>
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary"
              :loading="submittingChanges"
              @click="confirmDeletion()"
            >
              <strong>Delete</strong>
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-row class="g-container" no-gutters>
      <v-col>
        <h2 class="ma-0 text-center">{{ $route.meta.title }}</h2>

        <div class="p-content-details my-2">
          <v-row class="justify-center">
            <v-col cols="12" sm="4" md="3">
              <i-instructions
                instructionKey="data-maintenance/trading-rules"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col cols="12">
              <v-card
                class="update-card mx-auto rounded-lg mt-2 mt-md-4 pb-0 pa-4 px-md-6 pt-md-6 mb-8"
              >
                <v-form
                  ref="form"
                  v-model="formState"
                  :disabled="formDisabled"
                  @submit.prevent
                >
                  <v-alert
                    dense
                    text
                    outlined
                    border="left"
                    elevation="1"
                    type="info"
                  >
                    <div v-if="hasPreviousTradingRules" class="text-center">
                      <span
                        >These Supplier Trading Rules will take effect after
                        midnight.</span
                      >
                      <br />
                      <!-- Temporarily disabled link to previous rules -->
                    </div>
                    <div v-else class="text-center">
                      Changes to Supplier Trading Rules will take effect after
                      midnight only
                    </div>
                  </v-alert>

                  <v-divider class="mx-0 mt-6 mb-2" horizontal></v-divider>

                  <v-row>
                    <v-col cols="12">
                      <p class="g-bold g-text-color-black g-heading-s">
                        Geography
                      </p>
                      <legacy-radio-group
                        :disabled="formDisabled"
                        name="trading-rules-geography"
                        :default-col-props="{
                          cols: '12',
                          md: '4',
                          class: 'pb-0'
                        }"
                        :options="[
                          { label: 'National', value: NATIONAL },
                          { label: 'State', value: STATE },
                          { label: 'Zone', value: ZONE }
                        ]"
                        v-model="visibleType"
                        @change="changeGeography()"
                      />
                    </v-col>

                    <v-col cols="12">
                      <p class="g-bold g-text-color-black g-heading-s">
                        Geography costs
                      </p>

                      <v-alert
                        v-if="hasZoneWithoutRequiredValues"
                        border="left"
                        dense
                        outlined
                        text
                        type="info"
                        elevation="1"
                        class="my-2 i-alert"
                      >
                        <span>Make sure all values are provided to save</span>
                      </v-alert>
                      <v-alert
                        v-else-if="visibleType !== NATIONAL"
                        border="left"
                        dense
                        outlined
                        text
                        type="info"
                        elevation="1"
                        class="my-2 i-alert"
                      >
                        <span
                          >Freight must be > {{ currencySymbol }}0 in all cells
                          to SAVE.<br />
                          Ensures freight applies if/when a product is ranged to
                          a new State or Zone
                        </span>
                      </v-alert>

                      <table class="mt-3 data-table-zone-pricing">
                        <tbody>
                          <tr
                            :key="zone.zoneCode"
                            v-for="zone in scopedZonePrices"
                          >
                            <td class="text-center p-table-prices-zone">
                              <table width="100%">
                                <tbody>
                                  <tr>
                                    <td colspan="2" class="text-left">
                                      <p
                                        class="g-bold g-text-color-black g-heading-xs py-2"
                                      >
                                        {{ getZoneName(zone) }}
                                      </p>
                                    </td>
                                  </tr>
                                  <tr
                                    v-if="
                                      zone.minimumOrderValue !== null &&
                                      zone.minimumOrderValue ===
                                        zone.minimumOrderValueFreeFreight
                                    "
                                  >
                                    <td colspan="2">
                                      <v-alert
                                        border="left"
                                        dense
                                        outlined
                                        text
                                        type="info"
                                        elevation="1"
                                        class="my-2 i-alert"
                                      >
                                        <span
                                          >Freight must = {{ currencySymbol }}0
                                          when Minimum Order & Free Freight
                                          values are equal</span
                                        >
                                      </v-alert>
                                    </td>
                                  </tr>

                                  <!-- Minimum Order -->
                                  <tr>
                                    <td class="p-table-prices-zone-label">
                                      <p class="g-bold g-text-color-black pt-2">
                                        Minimum Order (Total Value)
                                        <v-icon
                                          class="i-tooltip-color pb-1"
                                          small
                                          v-tooltip="minimumOrderTooltipText"
                                        >
                                          mdi-help-circle-outline
                                        </v-icon>
                                      </p>
                                    </td>
                                    <td class="p-table-prices-zone-input">
                                      <div class="field-inline">
                                        <i-numeric-field
                                          background-color="#F0F0F0"
                                          :disabled="formDisabled"
                                          dense
                                          :label="minimumOrderInputLabel"
                                          :loading="validatingValue"
                                          :min="0"
                                          :max="9999.99"
                                          outlined
                                          :precision="2"
                                          :prefix="currencySymbol"
                                          required
                                          :rules="[
                                            REQUIRE_VALUE(
                                              zone.minimumOrderValue
                                            ),
                                            VALID_MOV(zone, isUSCountry)
                                          ]"
                                          :value="zone.minimumOrderValue"
                                          @input="
                                            (quantity) =>
                                              validateValue(
                                                zone.zoneCode,
                                                quantity,
                                                setMinimumOrderValue
                                              )
                                          "
                                        />
                                        <v-btn
                                          v-if="
                                            showApplyForZone(zone) &&
                                            isValidNumber(
                                              zone.minimumOrderValue
                                            )
                                          "
                                          small
                                          color="secondary"
                                          class="apply-all-btn white--text"
                                          :disabled="formDisabled"
                                          @click="
                                            applyToAll(
                                              'minimumOrderValue',
                                              zone.minimumOrderValue
                                            )
                                          "
                                        >
                                          Apply to all
                                        </v-btn>
                                      </div>
                                    </td>
                                  </tr>

                                  <!-- Free Freight -->
                                  <tr>
                                    <td class="p-table-prices-zone-label">
                                      <p class="g-bold g-text-color-black pt-2">
                                        Free Freight (Total Value)
                                        <v-icon
                                          class="i-tooltip-color pb-1"
                                          small
                                          v-tooltip="minimumOrderTooltipText"
                                        >
                                          mdi-help-circle-outline
                                        </v-icon>
                                      </p>
                                    </td>
                                    <td class="p-table-prices-zone-input">
                                      <div class="field-inline">
                                        <i-numeric-field
                                          background-color="#F0F0F0"
                                          class="pb-0"
                                          dense
                                          :disabled="
                                            formDisabled ||
                                            zone.minimumOrderValueFreeFreightNotApplicable
                                          "
                                          hide-details="auto"
                                          :label="
                                            zone.minimumOrderValueFreeFreightNotApplicable
                                              ? 'N/A'
                                              : freeFreightInputLabel
                                          "
                                          :loading="validatingValue"
                                          :min="0"
                                          :max="9999.99"
                                          outlined
                                          :precision="2"
                                          :prefix="currencySymbol"
                                          :required="
                                            !zone.minimumOrderValueFreeFreightNotApplicable
                                          "
                                          :rules="[
                                            CONDITIONALLY_REQUIRE_VALUE(
                                              zone.minimumOrderValueFreeFreight,
                                              !zone.minimumOrderValueFreeFreightNotApplicable
                                            ),
                                            VALID_FREIGHT(zone, isUSCountry)
                                          ]"
                                          :value="
                                            zone.minimumOrderValueFreeFreight
                                          "
                                          @input="
                                            (quantity) =>
                                              validateValue(
                                                zone.zoneCode,
                                                quantity,
                                                setMinimumOrderValueFreeFreight
                                              )
                                          "
                                        />
                                        <v-btn
                                          v-if="
                                            showApplyForZone(zone) &&
                                            !zone.minimumOrderValueFreeFreightNotApplicable &&
                                            isValidNumber(
                                              zone.minimumOrderValueFreeFreight
                                            )
                                          "
                                          small
                                          color="secondary"
                                          class="apply-all-btn white--text"
                                          :disabled="formDisabled"
                                          @click="
                                            applyToAll(
                                              'minimumOrderValueFreeFreight',
                                              zone.minimumOrderValueFreeFreight
                                            )
                                          "
                                        >
                                          Apply to all
                                        </v-btn>
                                      </div>

                                      <div class="field-inline mt-1">
                                        <v-checkbox
                                          dense
                                          class="mt-0"
                                          :disabled="formDisabled"
                                          v-model="
                                            zone.minimumOrderValueFreeFreightNotApplicable
                                          "
                                          label="I don't need a Free Freight value"
                                          @change="
                                            ((zone.minimumOrderValueFreeFreight =
                                              null),
                                            onFieldChange())
                                          "
                                        />
                                        <v-btn
                                          v-if="
                                            showApplyForZone(zone) &&
                                            zone.minimumOrderValueFreeFreightNotApplicable
                                          "
                                          small
                                          color="secondary"
                                          class="apply-all-btn white--text"
                                          :disabled="formDisabled"
                                          @click="
                                            applyCheckboxToAll(
                                              'minimumOrderValueFreeFreightNotApplicable',
                                              zone.minimumOrderValueFreeFreightNotApplicable
                                            )
                                          "
                                        >
                                          Apply to all
                                        </v-btn>
                                      </div>
                                    </td>
                                  </tr>

                                  <!-- Freight per carton -->
                                  <tr>
                                    <td class="p-table-prices-zone-label">
                                      <p class="g-bold g-text-color-black pt-2">
                                        Additional Freight cost per carton
                                        <v-icon
                                          class="i-tooltip-color pb-1"
                                          small
                                          v-tooltip="{
                                            html: `additionalFreightCostPerCartonToolTipContent-${zone.displayName}`
                                          }"
                                        >
                                          mdi-help-circle-outline
                                        </v-icon>
                                        <span
                                          :id="`additionalFreightCostPerCartonToolTipContent-${zone.displayName}`"
                                        >
                                          Applies if
                                          {{ isILR ? 'Customer' : 'Retailer' }}
                                          orders between Minimum Order & Free
                                          Freight values <br />
                                          {{ freightTooltipSuffix }}
                                        </span>
                                      </p>
                                    </td>
                                    <td class="p-table-prices-zone-input">
                                      <div class="field-inline">
                                        <i-numeric-field
                                          background-color="#F0F0F0"
                                          :disabled="formDisabled"
                                          dense
                                          :label="pricingTag"
                                          :loading="validatingZoneCost"
                                          :min="0"
                                          :max="9999.99"
                                          outlined
                                          :precision="2"
                                          :prefix="currencySymbol"
                                          required
                                          :rules="[
                                            REQUIRE_VALUE(
                                              zone.freightPerCartonExGst
                                            ),
                                            VALID_ADDITIONAL_FREIGHT(
                                              zone,
                                              'freightPerCartonExGst',
                                              isUSCountry
                                            )
                                          ]"
                                          :value="zone.freightPerCartonExGst"
                                          @input="
                                            (price) =>
                                              validateValue(
                                                zone.zoneCode,
                                                price,
                                                setFreightPerCartonCost
                                              )
                                          "
                                        />
                                        <v-btn
                                          v-if="
                                            showApplyForZone(zone) &&
                                            isValidNumber(
                                              zone.freightPerCartonExGst
                                            )
                                          "
                                          small
                                          color="secondary"
                                          class="apply-all-btn white--text"
                                          :disabled="formDisabled"
                                          @click="
                                            applyToAll(
                                              'freightPerCartonExGst',
                                              zone.freightPerCartonExGst
                                            )
                                          "
                                        >
                                          Apply to all
                                        </v-btn>
                                      </div>
                                    </td>
                                  </tr>

                                  <!-- Freight per pallet -->
                                  <tr>
                                    <td class="p-table-prices-zone-label">
                                      <p class="g-bold g-text-color-black pt-2">
                                        Additional Freight cost per pallet
                                        <v-icon
                                          class="i-tooltip-color pb-1"
                                          small
                                          v-tooltip="{
                                            html: `additionalFreightCostPerPalletToolTipContent-${zone.displayName}`
                                          }"
                                        >
                                          mdi-help-circle-outline
                                        </v-icon>
                                        <span
                                          :id="`additionalFreightCostPerPalletToolTipContent-${zone.displayName}`"
                                        >
                                          Applies if
                                          {{ isILR ? 'Customer' : 'Retailer' }}
                                          orders between Minimum Order & Free
                                          Freight values <br />
                                          {{ freightTooltipSuffix }}
                                        </span>
                                      </p>
                                    </td>
                                    <td class="p-table-prices-zone-input">
                                      <div class="field-inline">
                                        <i-numeric-field
                                          background-color="#F0F0F0"
                                          :disabled="formDisabled"
                                          dense
                                          hide-details="auto"
                                          :label="pricingTag"
                                          :loading="validatingZoneCost"
                                          :min="0"
                                          :max="9999.99"
                                          outlined
                                          :precision="2"
                                          :prefix="currencySymbol"
                                          required
                                          :rules="[
                                            REQUIRE_PALLET_VALUE(
                                              zone.freightPerPalletExGst
                                            ),
                                            VALID_ADDITIONAL_FREIGHT(
                                              zone,
                                              'freightPerPalletExGst',
                                              isUSCountry
                                            )
                                          ]"
                                          :value="
                                            zone.freightPerPalletExGst != null
                                              ? zone.freightPerPalletExGst
                                              : 0
                                          "
                                          @input="
                                            (price) =>
                                              validateValue(
                                                zone.zoneCode,
                                                price,
                                                setFreightPerPalletCost
                                              )
                                          "
                                        />
                                        <v-btn
                                          v-if="
                                            showApplyForZone(zone) &&
                                            isValidNumber(
                                              zone.freightPerPalletExGst
                                            )
                                          "
                                          small
                                          color="secondary"
                                          class="apply-all-btn white--text"
                                          :disabled="formDisabled"
                                          @click="
                                            applyToAll(
                                              'freightPerPalletExGst',
                                              zone.freightPerPalletExGst
                                            )
                                          "
                                        >
                                          Apply to all
                                        </v-btn>
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </v-col>
                  </v-row>

                  <v-card-actions
                    class="py-4 py-md-6 pa-0 justify-center p-action-sticky-bottom"
                  >
                    <template v-if="displayDeleteButton">
                      <v-btn
                        left
                        rounded
                        class="i-btn-block-red"
                        @click="confirmDeletionDialog = true"
                      >
                        Delete upcoming
                      </v-btn>
                    </template>
                    <v-spacer
                      v-if="!$vuetifyDisplay.smAndDown || displayDeleteButton"
                    ></v-spacer>
                    <template v-if="hasFreightInfo">
                      <template v-if="!editForm">
                        <v-btn
                          rounded
                          class="i-btn-block-secondary"
                          @click="editForm = !editForm"
                        >
                          Edit
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn
                          rounded
                          text
                          class="i-btn-text-black"
                          @click="loadFreightInfo"
                        >
                          Cancel
                        </v-btn>

                        <v-btn
                          rounded
                          class="i-btn-block-secondary"
                          :disabled="!validChanges"
                          :loading="validatingChanges || submittingChanges"
                          @click="validateSubmitForm"
                        >
                          Save
                        </v-btn>
                      </template>
                    </template>

                    <template v-else>
                      <v-btn
                        rounded
                        class="i-btn-block-secondary"
                        :disabled="!validChanges"
                        :loading="validatingChanges || submittingChanges"
                        @click="validateSubmitForm"
                      >
                        Save
                      </v-btn>
                    </template>
                  </v-card-actions>
                </v-form>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  deleteUpcomingFreightInfo,
  updateFreightInfo,
  validateUpdateFreightInfo
} from '@/services/freightInfo'
import iNumericField from '@/components/NumericField.vue'
import LegacyRadioGroup from '@/components/LegacyRadioGroup.vue'
import iPreviousTradingRules from '../components/PreviousTradingRules.vue'
import iUserActionDialog from '@/components/UserActionDialog.vue'
import iInstructions from '@/components/Instructions'
import { formatStateLabel as formatSupplierStateLabel } from '@/utils/stateDisplay'

const ACTION_TYPE = {
  CHANGE: 'CHANGE',
  SAVE: 'SAVE',
  PROCEED: 'PROCEED'
}

const DEBOUNCE_TIMER = 500
const NUM_DECIMALS = 2
const COUNTRY_REGION_US = 'US'

const RULES = {
  CONDITIONALLY_REQUIRE_VALUE: (v, condition) => {
    if (!condition) return true
    return v === 0 || !!v || 'Value required'
  },
  REQUIRE_VALUE: (v) => {
    return v === 0 || !!v || 'Value required'
  },
  REQUIRE_PALLET_VALUE: (v) => {
    return v !== null && v !== undefined && v !== '' ? true : 'Value required'
  },

  VALID_MOV: (zone, isUsCountry = false) => {
    if (zone.minimumOrderValueFreeFreightNotApplicable === true) return true
    return (
      +zone.minimumOrderValue <= +zone.minimumOrderValueFreeFreight ||
      `Must be lower or equal to Free Freight (Total Value)${isUsCountry ? '' : ' - inc Tax'}`
    )
  },
  VALID_FREIGHT: (zone, isUsCountry = false) => {
    if (zone.minimumOrderValueFreeFreightNotApplicable === true) return true
    return (
      +zone.minimumOrderValue <= +zone.minimumOrderValueFreeFreight ||
      `Must be higher or equal to Minimum Order (Total Value)${isUsCountry ? '' : ' - inc Tax'}`
    )
  },
  VALID_ADDITIONAL_FREIGHT: (
    zone,
    freightAttributeName,
    isUsCountry = false
  ) => {
    if (freightAttributeName === 'freightPerPalletExGst') {
      return true
    }
    if (zone.minimumOrderValueFreeFreightNotApplicable === true) {
      return +zone[freightAttributeName] > 0
        ? true
        : `Cannot be 0 if you don't have a Free Freight (Total Value)${isUsCountry ? '' : ' - inc Tax'}`
    } else if (+zone.minimumOrderValue === +zone.minimumOrderValueFreeFreight) {
      return +zone[freightAttributeName] === 0
        ? true
        : `Must be 0 if Free Freight (Total Value)${isUsCountry ? '' : ' - inc Tax'} is equal to Minimum Order (Total Value)${isUsCountry ? '' : ' - inc Tax'}`
    } else if (+zone.minimumOrderValue < +zone.minimumOrderValueFreeFreight) {
      return +zone[freightAttributeName] > 0
        ? true
        : `Cannot be 0 if Free Freight value is higher than Minimum Order (Total Value)${isUsCountry ? '' : ' - inc Tax'}`
    }

    return true
  }
}

const TYPE = {
  NATIONAL: 'NATIONAL',
  STATE: 'STATE',
  ZONE: 'ZONE'
}

export default {
  components: {
    LegacyRadioGroup,
    iInstructions,
    iNumericField,
    iPreviousTradingRules,
    iUserActionDialog
  },

  data() {
    return {
      actionDialog: false,
      actionText: null,
      actionType: null,
      confirmDeletionDialog: false,
      debounceValueChange: null,
      editForm: false,
      validatingChanges: false,
      submittingChanges: false,
      formState: false,
      mutableType: null,
      originalTradingRules: {},
      showPreviousTradingRules: false,
      validatingValue: false,
      validatingZoneCost: false,
      visibleType: null,
      mutableZonePrices: [],
      ...ACTION_TYPE,
      ...RULES,
      ...TYPE
    }
  },

  computed: {
    ...mapGetters('profile', ['readyToTrade']),
    ...mapGetters('freightInfo', [
      'hasPreviousTradingRules',
      'validTradingRules'
    ]),

    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('pricingRegions', ['pricingRegions']),

    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'tenantCurrency',
      'tenantLocale'
    ]),

    ...mapState('freightInfo', {
      previousTradingRules: (state) => state.infoPrevious,
      type: (state) => state.info?.type,
      zonePrices: (state) => state.info?.zonePrices
    }),

    displayDeleteButton() {
      return (
        this.hasFreightInfo && this.hasPreviousTradingRules && !this.editForm
      )
    },

    changesMade() {
      return !_.isEqual(this.originalTradingRules, this.createBluePrint())
    },

    hasFreightInfo() {
      return this.validTradingRules
    },

    isNationalFreight() {
      return this.mutableType === this.NATIONAL
    },

    isStateFreight() {
      return (self) => this.mutableType === this.STATE
    },

    isZoneFreight() {
      return this.mutableType === this.ZONE
    },

    pricingTag() {
      if (this.isNationalFreight)
        return this.isUSCountry ? 'National cost' : 'National cost (ex Tax)'
      if (this.mutableType === this.STATE)
        return this.isUSCountry ? 'State cost' : 'State cost (ex Tax)'
      if (this.isZoneFreight)
        return this.isUSCountry ? 'Zone cost' : 'Zone cost (ex Tax)'
      return this.isUSCountry
        ? 'Freight cost per carton'
        : 'Freight cost per carton (ex Tax)'
    },

    isUSCountry() {
      return this.tenantCountryRegion === COUNTRY_REGION_US
    },

    minimumOrderInputLabel() {
      return this.isUSCountry
        ? 'Minimum Order (Total Value)'
        : 'Minimum Order (Total Value) - inc Tax'
    },

    freeFreightInputLabel() {
      return this.isUSCountry
        ? 'Free Freight (Total Value)'
        : 'Free Freight (Total Value) - inc Tax'
    },

    minimumOrderTooltipText() {
      const taxText = this.isUSCountry ? 'taxes' : 'tax (if applicable)'
      return `Total Value includes ${taxText} but excluded Freight & Rebate charges. Applies when ${
        this.isILR ? 'Customer' : 'Retailer'
      } orders this value from Supplier in cart`
    },

    freightTooltipSuffix() {
      return this.isUSCountry
        ? 'Input value set in the shopping cart'
        : `Input value ex Tax - tax is applied in the ${
            this.isILR ? 'Customer' : 'Retailer'
          } shopping cart`
    },

    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    },

    scopedZonePrices() {
      if (this.isNationalFreight) return [this.mutableZonePrices[0]]
      if (this.mutableType === this.STATE) {
        return this.mutableZonePrices.reduce((states, currentZone) => {
          const additionalStateZone = states.find(
            (zone) => zone.state === currentZone.state
          )
          return additionalStateZone ? states : states.concat([currentZone])
        }, [])
      }
      if (this.isZoneFreight) return this.mutableZonePrices
      return []
    },

    hasZoneWithoutRequiredValues() {
      return this.mutableZonePrices.find(
        (zone) =>
          zone.freightPerCartonExGst === null ||
          zone.freightPerCartonExGst === undefined ||
          zone.freightPerPalletExGst === null ||
          zone.freightPerPalletExGst === undefined ||
          zone.minimumOrderValue === null ||
          (!zone.minimumOrderValueFreeFreightNotApplicable &&
            zone.minimumOrderValueFreeFreight === undefined)
      )
    },

    validChanges() {
      return this.formState && this.changesMade
    },

    formDisabled() {
      return this.hasFreightInfo && !this.editForm
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('freightInfo', ['fetchFreightInfo']),

    showApplyForZone(zone) {
      if (!zone || !zone.zoneCode) return false
      if (this.isStateFreight()) {
        return this.scopedZonePrices?.[0]?.zoneCode === zone.zoneCode
      }
      if (!this.isZoneFreight) return false
      return String(zone.zoneCode).toUpperCase().includes('ACT1')
    },
    isValidNumber(v) {
      if (v === 0) return true
      return (
        v !== null &&
        v !== undefined &&
        String(v).trim() !== '' &&
        !isNaN(Number(v))
      )
    },

    // ***
    // Functions related to validating and adjusting values
    // ***

    getNumberWithDecimals(value, numberDecimals) {
      if (value === '' || value === null || value === undefined) {
        return value === 0 || value === '0' ? 0 : null
      }
      const valueAsNumber = parseFloat(
        parseFloat(value).toFixed(numberDecimals)
      )
      return valueAsNumber < 0 ? null : valueAsNumber
    },

    validateValue(zoneCode, value, assignFunction) {
      this.validatingValue = true
      clearTimeout(this.debounceValueChange)
      this.debounceValueChange = setTimeout(() => {
        assignFunction(
          zoneCode,
          this.getNumberWithDecimals(value, NUM_DECIMALS)
        )
        this.onFieldChange()
        this.validatingValue = false
      }, DEBOUNCE_TIMER)
    },

    // ***
    // Functions related to help determining if Special has been modified
    // ***

    createBluePrint() {
      return {
        mutableType: this.mutableType,
        mutableZonePrices: this.mutableZonePrices
      }
    },

    // ***
    // Actions Dialog
    // ***

    changeGeography() {
      this.startAction('Warning', 'Confirm change of Geography', this.CHANGE)
    },

    cancelAction() {
      this.dismissDialog()
      this.validatingChanges = this.submittingChanges = false
    },

    deleteUpcomingRules() {
      this.submittingChanges = true
      deleteUpcomingFreightInfo(this.selectedSupplierId)
        .then(async () => {
          await this.fetchFreightInfo(this.selectedSupplierId)
          this.loadFreightInfo()
          this.originalTradingRules = _.cloneDeep(this.createBluePrint())
          await this.addNotification({
            color: 'secondary',
            text: 'Upcoming rules successfully deleted'
          })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Upcoming trading rules could not be deleted, please try again...'
          })
        })
        .finally(() => {
          this.submittingChanges = false
          this.confirmDeletionDialog = false
        })
    },

    dismissDialog() {
      this.actionDialog = false
      this.actionTitle = this.actionText = this.actionType = null
      this.visibleType = this.mutableType
    },

    confirmAction() {
      if (this.actionType === this.SAVE || this.actionType === this.PROCEED)
        this.submitForm()
      else if (this.actionType === this.CHANGE) {
        this.zeroZonePricesMap()
        this.mutableType = this.visibleType
      }
      this.dismissDialog()
    },

    confirmDeletion() {
      this.deleteUpcomingRules()
    },

    startAction(actionTitle, actionText, actionType) {
      this.actionTitle = actionTitle
      this.actionText = actionText
      this.actionType = actionType
      this.actionDialog = true
    },

    // ***
    // Functions related to price Map
    // ***

    generateZonePricesMap() {
      return this.pricingRegions?.map((zone) => {
        const zonePrice = this.findZonePrice(zone.zoneCode)
        return {
          ...zone,
          minimumOrderValueFreeFreight: zonePrice.minimumOrderValueFreeFreight,
          minimumOrderValueFreeFreightNotApplicable:
            zonePrice.minimumOrderValueFreeFreightNotApplicable,
          minimumOrderValue: zonePrice.minimumOrderValue,
          freightPerCartonExGst: zonePrice.freightPerCartonExGst,
          freightPerPalletExGst: zonePrice.freightPerPalletExGst ?? 0
        }
      })
    },

    getZoneName(zone) {
      if (this.isNationalFreight) return 'National'
      if (this.mutableType === this.STATE)
        return this.formatStateLabel(zone.state)
      if (this.isZoneFreight) return zone.displayName
      return null
    },

    formatStateLabel(stateCode) {
      return formatSupplierStateLabel(stateCode, this.tenantCountryRegion)
    },

    findZonePrice(zoneCode) {
      return this.zonePrices?.find((zone) => zone.zoneCode === zoneCode) || {}
    },

    setMinimumOrderValue(zoneCode, price) {
      const zone = this.mutableZonePrices.find(
        (zone) => zone.zoneCode === zoneCode
      )
      zone.minimumOrderValue = this.getNumberWithDecimals(price, NUM_DECIMALS)
      if (price && price === zone.minimumOrderValueFreeFreight) {
        this.applyFreeFreightForAllEmptyZonePrices()
      }
    },

    setMinimumOrderValueFreeFreight(zoneCode, price) {
      const zone = this.mutableZonePrices.find(
        (zone) => zone.zoneCode === zoneCode
      )
      zone.minimumOrderValueFreeFreight = this.getNumberWithDecimals(
        price,
        NUM_DECIMALS
      )
      if (price && price === zone.minimumOrderValue) {
        this.applyFreeFreightForAllEmptyZonePrices()
      }
    },

    setFreightPerCartonCost(zoneCode, price) {
      const zone = this.mutableZonePrices.find(
        (zone) => zone.zoneCode === zoneCode
      )
      zone.freightPerCartonExGst = this.getNumberWithDecimals(
        price,
        NUM_DECIMALS
      )
    },

    setFreightPerPalletCost(zoneCode, price) {
      const zone = this.mutableZonePrices.find(
        (zone) => zone.zoneCode === zoneCode
      )
      zone.freightPerPalletExGst = this.getNumberWithDecimals(
        price,
        NUM_DECIMALS
      )
    },

    setZonePrices() {
      if (this.isNationalFreight) {
        this.mutableZonePrices = this.mutableZonePrices.map((zone) => ({
          ...zone,
          minimumOrderValue: this.mutableZonePrices[0].minimumOrderValue,
          minimumOrderValueFreeFreight:
            this.mutableZonePrices[0].minimumOrderValueFreeFreight,
          minimumOrderValueFreeFreightNotApplicable:
            this.mutableZonePrices[0].minimumOrderValueFreeFreightNotApplicable,
          freightPerCartonExGst:
            this.mutableZonePrices[0].freightPerCartonExGst,
          freightPerPalletExGst: this.mutableZonePrices[0].freightPerPalletExGst
        }))
      }

      if (this.mutableType === this.STATE) {
        this.mutableZonePrices = this.mutableZonePrices.map((zone) => {
          const state = this.mutableZonePrices.find(
            (state) => state.state === zone.state
          )

          return {
            ...zone,
            minimumOrderValue: state.minimumOrderValue,
            minimumOrderValueFreeFreight: state.minimumOrderValueFreeFreight,
            minimumOrderValueFreeFreightNotApplicable:
              state.minimumOrderValueFreeFreightNotApplicable,
            freightPerCartonExGst: state.freightPerCartonExGst,
            freightPerPalletExGst: state.freightPerPalletExGst
          }
        })
      }
    },

    zeroZonePricesMap() {
      this.mutableZonePrices = this.mutableZonePrices.map((zone) => ({
        ...zone,
        minimumOrderValue: null,
        minimumOrderValueFreeFreight: null,
        minimumOrderValueFreeFreightNotApplicable: false,
        freightPerCartonExGst: null,
        freightPerPalletExGst: 0
      }))
      this.$refs.form.resetValidation()
    },

    // ***
    // Form lifecycle
    // **

    loadFreightInfo() {
      this.editForm = false
      this.mutableType = this.visibleType = this.type || this.NATIONAL
      this.mutableZonePrices = this.generateZonePricesMap()
    },

    applyFreeFreightForAllEmptyZonePrices() {
      this.mutableZonePrices.forEach((zonePrice) => {
        if (!zonePrice.freightPerCartonExGst) {
          zonePrice.freightPerCartonExGst = 0
          zonePrice.freightPerPalletExGst = 0
        }
      })
    },

    onFieldChange() {
      this.$refs.form.validate()
    },

    createUpdateFreightInfoRequest() {
      return {
        type: this.mutableType,
        zonePrices: this.mutableZonePrices
      }
    },

    validateSubmitForm() {
      this.validatingChanges = true
      this.setZonePrices()

      const request = this.createUpdateFreightInfoRequest()
      validateUpdateFreightInfo(this.selectedSupplierId, request)
        .then((response) => {
          if (response.hasIncompatibleCoupons) {
            const activeCoupons = response.incompatibleActiveCouponRules
            const upcomingCoupons = response.incompatibleUpcomingCouponRules
            const activeCouponsText =
              activeCoupons.length > 0
                ? ` <strong>${activeCoupons.length}</strong> ACTIVE coupons`
                : ''
            const upcomingCouponsText =
              upcomingCoupons.length > 0
                ? ` <strong>${upcomingCoupons.length}</strong> UPCOMING coupons`
                : ''
            const activeCouponsImpactText =
              activeCoupons.length > 0
                ? ` the incompatible ACTIVE coupons will be ended today`
                : ''
            const upcomingCouponsImpactText =
              upcomingCoupons.length > 0
                ? ` the incompatible UPCOMING coupons will be deleted`
                : ''
            const joiningText =
              activeCoupons.length > 0 && upcomingCoupons.length > 0
                ? ' and'
                : ''
            this.startAction(
              'Update Trading Rules',
              `We found ${activeCouponsText}${joiningText}${upcomingCouponsText} ` +
                'that are incompatible with your new Trading Rules.<br/><br/>If you wish to proceed,' +
                `${activeCouponsImpactText}${joiningText}${upcomingCouponsImpactText}.<br/><br/>Would you still like to proceed?`,
              this.PROCEED
            )
          } else {
            this.startAction(
              'Update Trading Rules',
              'Are you sure that you want to update your trading rules?',
              this.SAVE
            )
          }
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Trading rules could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.validatingChanges = false
        })
    },

    submitForm() {
      this.submittingChanges = true
      this.setZonePrices()
      const request = this.createUpdateFreightInfoRequest()
      updateFreightInfo(this.selectedSupplierId, request)
        .then(async () => {
          await this.fetchFreightInfo(this.selectedSupplierId)
          this.loadFreightInfo()
          this.originalTradingRules = _.cloneDeep(this.createBluePrint())
          this.addNotification({
            color: 'secondary',
            text: 'Trading rules successfully updated'
          })

          if (!this.readyToTrade)
            await this.$router.push({
              name: 'dashboard',
              query: { showTradingSteps: 'true' }
            })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Trading rules could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.submittingChanges = false
        })
    },

    // ***
    // Apply-to-all helpers (ACT1 only, Zone view)
    // ***

    applyToAll(fieldKey, value) {
      // Normalize numeric values where needed
      const numericKeys = new Set([
        'minimumOrderValue',
        'minimumOrderValueFreeFreight',
        'freightPerCartonExGst',
        'freightPerPalletExGst'
      ])
      const normalized =
        numericKeys.has(fieldKey) && value !== null && value !== undefined
          ? this.getNumberWithDecimals(value, NUM_DECIMALS)
          : value

      const sourceRow = this.scopedZonePrices?.[0]
      ;(this.mutableZonePrices || []).forEach((z) => {
        const isZoneSourceRow = String(z.zoneCode || '')
          .toUpperCase()
          .includes('ACT1')

        if (this.isNationalFreight) {
          this.$set(z, fieldKey, normalized)
        } else if (this.isStateFreight()) {
          if (sourceRow?.state) {
            this.$set(z, fieldKey, normalized)
          }
        } else if (this.isZoneFreight && !isZoneSourceRow) {
          this.$set(z, fieldKey, normalized)
        }
      })
      this.onFieldChange()
    },

    applyCheckboxToAll(fieldKey, value) {
      ;(this.mutableZonePrices || []).forEach((z) => {
        const isZoneSourceRow = String(z.zoneCode || '')
          .toUpperCase()
          .includes('ACT1')

        if (this.isNationalFreight) {
          this.$set(z, fieldKey, value)
        } else if (this.isStateFreight()) {
          this.$set(z, fieldKey, value)
        } else if (this.isZoneFreight && !isZoneSourceRow) {
          this.$set(z, fieldKey, value)
        }

        if (fieldKey === 'minimumOrderValueFreeFreightNotApplicable' && value) {
          this.$set(z, 'minimumOrderValueFreeFreight', null)
        }
      })
      this.onFieldChange()
    }
  },

  mounted() {
    this.loadFreightInfo()
    this.originalTradingRules = _.cloneDeep(this.createBluePrint())
  }
}
</script>

<style lang="scss" scoped>
#trading-rules {
  hr {
    min-width: 100%;
    margin-top: 0;
    margin-bottom: 25px;
  }

  .p-action-sticky-bottom {
    background-color: white;
    position: sticky;
    bottom: 0px;
    border-top: 1px solid lightgray !important;
    margin-left: 0;
    width: 100%;
  }

  .p-content-details {
    max-width: 740px;
    margin: auto;
  }

  .p-table-prices-zone {
    width: 40%;
    border-bottom: 1px solid lightgrey;
    padding-bottom: 8px !important;
  }

  .field-inline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .apply-all-btn {
    min-width: 0;
    padding-left: 6px !important;
    padding-right: 6px !important;
  }
}

@media only screen and (min-width: 960px) {
  #trading-rules {
    .p-table-prices-zone {
      width: 50%;
    }
  }
}
</style>

<style lang="scss">
#trading-rules {
  .data-table-zone-pricing {
    margin-bottom: -16px;

    td {
      padding: 0 4px !important;
      vertical-align: middle;

      &.p-table-prices-zone-label {
        width: 40%;
        vertical-align: top;
        text-align: right;
      }

      &.p-table-prices-zone-input {
        padding-left: 10px !important;
      }
    }

    .v-text-field__details {
      padding: 0 4px !important;
      margin-bottom: 0 !important;
    }

    .v-messages {
      .v-messages__message {
        font-size: 0.9rem;
        margin-bottom: 4px !important;
      }
    }
  }
}
</style>
